import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  updateProfile
} from 'firebase/auth';
import app from '../firebase';
import { logLogin } from '../services/telemetry';
import {
  loadKeepSignedInPreference,
  saveKeepSignedInPreference,
  saveSavedEmail
} from './authPreferences';

const AuthContext = createContext(null);

async function applyAuthPersistence(keepSignedIn) {
  const auth = getAuth(app);
  await setPersistence(
    auth,
    keepSignedIn ? browserLocalPersistence : browserSessionPersistence
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [profileStamp, setProfileStamp] = useState(0);

  useEffect(() => {
    const auth = getAuth(app);
    let unsubscribe = () => {};

    applyAuthPersistence(loadKeepSignedInPreference())
      .catch(() => {})
      .finally(() => {
        unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
          setUser(firebaseUser);
          setAuthReady(true);
        });
      });

    return () => unsubscribe();
  }, []);

  const signIn = async (email, password, options = {}) => {
    const keepSignedIn = options.rememberMe ?? loadKeepSignedInPreference();
    saveKeepSignedInPreference(keepSignedIn);
    await applyAuthPersistence(keepSignedIn);

    const auth = getAuth(app);
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (keepSignedIn) {
      saveSavedEmail(email);
    }
    logLogin(result.user.uid);
    return result;
  };

  const signUp = async ({ name, email, password }, options = {}) => {
    const keepSignedIn = options.rememberMe ?? true;
    saveKeepSignedInPreference(keepSignedIn);
    await applyAuthPersistence(keepSignedIn);

    const auth = getAuth(app);
    const result = await createUserWithEmailAndPassword(auth, email, password);

    if (name) {
      await updateProfile(result.user, { displayName: name });
    }
    if (keepSignedIn) {
      saveSavedEmail(email);
    }
    logLogin(result.user.uid);
    return result;
  };

  const signOutUser = async () => {
    const auth = getAuth(app);
    return signOut(auth);
  };

  const refreshProfile = useCallback(async () => {
    const auth = getAuth(app);
    if (!auth.currentUser) return null;
    await auth.currentUser.reload();
    setUser(auth.currentUser);
    setProfileStamp((stamp) => stamp + 1);
    return auth.currentUser;
  }, []);

  const resetPassword = async (email) => {
    const auth = getAuth(app);
    return sendPasswordResetEmail(auth, email);
  };

  const signInWithGoogle = async (options = {}) => {
    const keepSignedIn = options.rememberMe ?? loadKeepSignedInPreference();
    saveKeepSignedInPreference(keepSignedIn);
    await applyAuthPersistence(keepSignedIn);

    const auth = getAuth(app);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const result = await signInWithPopup(auth, provider);
    if (keepSignedIn && result.user?.email) {
      saveSavedEmail(result.user.email);
    }
    logLogin(result.user.uid);
    return result;
  };

  const value = useMemo(
    () => ({
      user,
      authReady,
      signIn,
      signUp,
      signInWithGoogle,
      resetPassword,
      signOut: signOutUser,
      refreshProfile
    }),
    [user, authReady, profileStamp, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
